import {
  OrderByInput,
  useFileListOrder,
  useSetFileListOrder,
} from '@gripp/shared-logic';
import {
  Colors,
  InfiniteDataGrid,
  SelectionStatus,
} from '@gripp/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { useColumnsFile } from './useColumnsFile';

export type SelectedFileRow = {
  id: string;
};

type FilesListProps = {
  filter: any;
  showCheckboxColumn: boolean;
  countButton: (data: any) => React.JSX.Element;
  isProducerWorkspace?: boolean;
  onRowsSelectedChange?: (rows: SelectedFileRow[]) => void;
  selectedRowIds?: string[];
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  onItemsCountChange?: (count: number) => void;
};

export const FilesList = ({
  filter,
  showCheckboxColumn,
  countButton,
  isProducerWorkspace = false,
  onRowsSelectedChange,
  selectedRowIds,
  selectionStatus,
  onSelectionStatusChange,
  onItemsCountChange,
}: FilesListProps) => {
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(64, 0, 90);
  const { width } = useWindowDimensions();
  const navigate = useNavigate();

  const { data: fileOrder } = useFileListOrder();
  const { mutateAsync: setFileOrder } = useSetFileListOrder();

  const updateRowsSelected = (rows: any[]) => {
    onRowsSelectedChange?.(rows.map((row) => ({ id: row.id })));
  };

  const selectedIdsSet = useMemo(
    () => new Set(selectedRowIds ?? []),
    [selectedRowIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const onOrderByChange = (orderBy: OrderByInput) => {
    setFileOrder(orderBy);
  };

  const handleFileNameClick = (file: any) => {
    navigate(`/files/${file.id}`);
  };

  const columns = useColumnsFile(
    showCheckboxColumn,
    countButton,
    isProducerWorkspace,
    handleFileNameClick
  ) as ColumnDef<any, any>[];

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <View style={[styles.textWrapper, { maxWidth: width - 10 }]}>
        <Text style={styles.emptyTapText} numberOfLines={2}>
          Tap '+ Create File' to add your first file.
        </Text>
      </View>
    </View>
  );

  return (
    <View
      style={[
        styles.container,
        {
          maxHeight: containerMaxHeight,
        },
        styles.containerCropCircle,
      ]}
    >
      <InfiniteDataGrid
        columns={columns}
        query={LIST_FILES}
        dataPrefix="media.listFiles"
        modelName="media"
        orderBy={fileOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        listMaxHeight={listMaxHeight}
        showScrollIndicator={true}
        onOrderByChange={onOrderByChange}
        textEmpty={renderEmpty()}
        showEmptyTable
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        exportFileName="files-export"
        onItemsCountChange={onItemsCountChange}
      />
    </View>
  );
};

const LIST_FILES = `
  query FindFiles($filter: ListFilesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    media {
      listFiles(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          name
          productsCount
          assetsCount
          addonsCount
          categories {
            id
            name
          }
          updatedAt
          createdAt
          type
          extension
          url
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  containerCropCircle: {
    borderRadius: 0,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  textWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTapText: {
    fontSize: 22,
    fontWeight: '700',
    color: Colors.blackText,
    textAlign: 'center',
  },
});
